import dayjs from "dayjs";

export const defaultDatetimeFormat = "YYYY-MM-DD";
// Used for transforming datetimes into a format like "12:56 PM, Jul 15, 2020"
export const dateWithTimeFormat = "h:mm A, MMM D, YYYY";

const DATE_TIME_FORMAT = "DD MMM YYYY HH:mm";

type Timestamp = string | number | Date;

/**
 * This function wraps dayjs because its annoying to import it. If you need
 * to format any datetime string, use this function.
 *
 * @param datetime
 * Required.
 * This is the actual full datetime string from the backend.
 * Example: 2019-07-02T17:46:08.063Z
 * @param format Optional.
 * Format of the datetime you want to display to the user.
 * The default is set to YYYY-MM-DD (ISO_8601) so you probably dont need to pass in a new one.
 */
export const formatDatetime = (
  datetime: Timestamp,
  format: string = defaultDatetimeFormat,
) => {
  const formattedDate: string = dayjs(datetime).format(format);
  return formattedDate;
};

/**
 * returns unix timestamp (the number of seconds since the Unix Epoch)
 * Example: 1562175656
 */
export const getUnixTimestamp = () => {
  return dayjs().unix();
};

/**
 * Returns a sort function that you would use when trying to sort an array of object.
 * See unit test for full usage.
 *
 * Usage: array.sort(sortUnixTimestampByKey("timestamp"));
 *
 * @param key Required. This is the key of the object where the timestamp is
 */
export const sortUnixTimestampByKey = <A extends { [key: string]: number }>(
  key: keyof A,
) => {
  return function sortFunction(a: A, b: A) {
    return b[key] - a[key];
  };
};

/**
 * timeElapsed takes a start time and starts counting seconds elapsed.
 *
 * Rules:
 * - >=1d will show days, hours, minutes and seconds
 * - <=23h59m59s shows only hours, minutes and seconds
 * - <=59m5ms shows only minutes and seconds
 * - <=59s shows only seconds
 *
 * @param startTime
 */
export const timeElapsed = (startTime: Timestamp, nowTime?: Timestamp) => {
  const now = nowTime === undefined ? Date.now() : new Date(nowTime).getTime();
  const start = new Date(startTime).getTime();
  const diff = (now - start) / 1000;
  const flooredTime = Math.floor(diff);
  if (diff <= 0) {
    return "0s";
  }

  const seconds = flooredTime % 60;
  const minutes = flooredTime / 60;
  const hours = flooredTime / 3600;
  const days = Math.floor(flooredTime / (24 * 3600));

  const flooredMinutes = Math.floor(minutes) % 60;
  const flooredHours = Math.floor(hours) % 24;
  const flooredDays = Math.floor(days);

  if (!flooredDays && !flooredHours && minutes < 1) {
    return `${seconds}s`;
  }

  if (!flooredDays && hours < 1) {
    return `${flooredMinutes}m ${seconds}s`;
  }

  if (flooredDays < 1) {
    return `${flooredHours}h ${flooredMinutes}m ${seconds}s`;
  }

  return `${days}d ${flooredHours}h ${flooredMinutes}m ${seconds}s`;
};

export function Timestamp({
  timestamp,
  format = DATE_TIME_FORMAT,
}: {
  timestamp: Timestamp | undefined;
  format?: string;
}) {
  return <>{timestamp ? formatDatetime(timestamp, format) : "--"}</>;
}

/**
 * Returns the number of days the start date is from the end date. The dates are
 * floored by day so hours will not be taken into account.
 *
 * Example: daysFrom("9/26/2020 09:10:37"", "9/21/2020 11:52:41") = 5
 */
export function daysFrom(startDate: Timestamp, endDate: Timestamp) {
  const flooredStartDate = dayjs(startDate).endOf("d");
  const flooredEndDate = dayjs(endDate).endOf("d");
  return flooredStartDate.diff(flooredEndDate, "d");
}
